import { getApiUrl } from '@/lib/api-config';
import { uploadEntityAttachments, fetchEntityAttachments, updateAttachment, deleteAttachment } from '@/lib/entity-attachments';

export interface ClientAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

export async function uploadClientAttachments(
  clientId: string | undefined,
  files: File[],
  companyId: string,
  userId: string
): Promise<ClientAttachment[]> {
  return uploadEntityAttachments('user', clientId, files, companyId, userId, { metadata: { document_type: 'client_document' } });
}

export function deleteClientAttachment(
  attachmentId: string,
  userId: string | undefined
): Promise<boolean> {
  return deleteAttachment(attachmentId, userId);
}

export async function fetchClientAttachments(
  recordId: string,
  companyId: string
): Promise<ClientAttachment[]> {
  if (!recordId || !companyId) return [];
  return fetchEntityAttachments('user', recordId, { documentType: 'client_document', ascending: false });
}

/**
 * BUG-01 fix (2026-09-18): shared client-creation logic, extracted so the
 * "+ Add new client" flow inside ProjectForm's ClientCombobox (creating a
 * client without leaving the project form) can create a real client the
 * same way the Clients page itself does. ONE call (POST api/entities/user
 * with user_type 'client'): the server creates the person AND their role in
 * this company together, so a failure leaves no half-created client.
 */
export async function createClientUser(
  companyId: string,
  actorUserId: string,
  formData: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    company_name?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zip_code?: string;
    notes?: string;
  }
): Promise<{ id: string; name: string }> {
  const userResponse = await fetch(getApiUrl('api/entities/user'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: actorUserId,
      company_id: companyId,
      user_type: 'client', // the server creates the client's role in this company with the person (leads.create); never stored
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
      company_name: formData.company_name,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      zip_code: formData.zip_code,
      lead_status: 'active_client',
      notes: formData.notes,
    }),
  });

  if (!userResponse.ok) {
    const errorData = await userResponse.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to save client');
  }

  const newUser = (await userResponse.json()).data;

  const name = formData.company_name || `${formData.first_name} ${formData.last_name}`.trim() || formData.email;
  return { id: newUser.local_id, name };
}

